import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import swal from "sweetalert";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";
import usePermission from "../../../hooks/usePermission";
import {
 createStatutoryApproval,
 deleteStatutoryApproval,
 downloadStatutoryApprovalPdf,
 getStatutoryApprovals,
 getStatutoryApprovalStats,
 updateStatutoryApproval,
} from "../../../services/statutoryApprovalService";

const APPROVAL_TYPES = [
 "Fire NOC",
 "CEIG",
 "Structural Stability Certificate",
 "Factory License",
 "Pollution Control",
 "Lift License",
 "Electrical Safety Certificate",
 "Other",
];

const STATUSES = ["Pending", "Submitted", "Approved", "Rejected", "Expired", "Renewal Due", "Not Applicable"];

const emptyForm = {
 project: "",
 workOrder: "",
 approvalType: "Fire NOC",
 authorityName: "",
 certificateNo: "",
 issueDate: "",
 validTill: "",
 renewalDueDate: "",
 status: "Pending",
 remarks: "",
};

const statusVariant = (status) => {
 const value = status || "";
 if (value === "Approved") return "success";
 if (value === "Expired" || value === "Rejected") return "danger";
 if (value === "Renewal Due" || value === "Pending" || value === "Submitted") return "warning";
 if (value === "Not Applicable") return "secondary";
 return "light";
};

const fmtDate = (date) => (date ? new Date(date).toLocaleDateString("en-IN") : "-");

const StatutoryApprovals = () => {
 const { hasPermission } = usePermission();
 const [records, setRecords] = useState([]);
 const [stats, setStats] = useState(null);
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [loading, setLoading] = useState(false);
 const [showForm, setShowForm] = useState(false);
 const [editing, setEditing] = useState(null);
 const [form, setForm] = useState(emptyForm);
 const [files, setFiles] = useState([]);
 const [downloadingPdf, setDownloadingPdf] = useState("");
 const [filters, setFilters] = usePersistentFilters("statutory_approvals_filters", { search: "", status: "", approvalType: "", project: "" });
 const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0, limit: 10 });

 const visibleWorkOrders = useMemo(() => {
    if (!form.project) return workOrders;
    return workOrders.filter(
      (wo) =>
        String(wo.project?._id || wo.project) === String(form.project) ||
        (form.workOrder && String(wo._id) === String(form.workOrder))
    );
  }, [form.project, form.workOrder, workOrders]);

 const canCreateStatutoryApproval =
 hasPermission("create:statutory-approvals") || hasPermission("create:documents");
 const canEditStatutoryApproval =
 hasPermission("edit:statutory-approvals") || hasPermission("edit:documents");
 const canDeleteStatutoryApproval =
 hasPermission("delete:statutory-approvals") || hasPermission("delete:documents");

 const fetchRefs = useCallback(async () => {
 try {
 const [projectRes, workOrderRes] = await Promise.all([
 api.get("/projects?limit=1000"),
 api.get("/work-orders?limit=1000"),
 ]);
 setProjects(projectRes.data?.data || []);
 setWorkOrders(workOrderRes.data?.data || []);
 } catch (_) {}
 }, []);

 const fetchStats = useCallback(async () => {
 try {
 const res = await getStatutoryApprovalStats();
 if (res.data?.success) setStats(res.data.data);
 } catch (_) {}
 }, []);

 const fetchRecords = useCallback(async () => {
 setLoading(true);
 try {
 const params = {
 page: pagination.currentPage,
 limit: pagination.limit,
 ...(filters.search && { search: filters.search }),
 ...(filters.status && { status: filters.status }),
 ...(filters.approvalType && { approvalType: filters.approvalType }),
 ...(filters.project && { project: filters.project }),
 };
 const res = await getStatutoryApprovals(params);
 setRecords(res.data?.data || []);
 setPagination((prev) => ({
 ...prev,
 currentPage: res.data?.pagination?.currentPage || 1,
 totalPages: res.data?.pagination?.totalPages || 1,
 totalCount: res.data?.pagination?.totalCount || 0,
 }));
 } catch (_) {
 toast.error("Failed to load statutory approvals");
 } finally {
 setLoading(false);
 }
 }, [filters, pagination.currentPage, pagination.limit]);

 useEffect(() => {
 fetchRefs();
 fetchStats();
 }, [fetchRefs, fetchStats]);

 useEffect(() => {
 fetchRecords();
 }, [fetchRecords]);

 const resetForm = () => {
 setEditing(null);
 setForm(emptyForm);
 setFiles([]);
 };

 const openCreate = () => {
 resetForm();
 setShowForm(true);
 };

 const openEdit = (record) => {
 setEditing(record);
 setForm({
 project: record.project?._id || record.project || "",
 workOrder: record.workOrder?._id || record.workOrder || "",
 approvalType: record.approvalType || "Fire NOC",
 authorityName: record.authorityName || "",
 certificateNo: record.certificateNo || "",
 issueDate: record.issueDate ? record.issueDate.slice(0, 10) : "",
 validTill: record.validTill ? record.validTill.slice(0, 10) : "",
 renewalDueDate: record.renewalDueDate ? record.renewalDueDate.slice(0, 10) : "",
 status: record.status || "Pending",
 remarks: record.remarks || "",
 });
 setFiles([]);
 setShowForm(true);
 };

 const handleSubmit = async (event) => {
 event.preventDefault();
 if (!form.project) {
 toast.error("Project is required");
 return;
 }

 const data = new FormData();
 Object.entries(form).forEach(([key, value]) => {
 if (value !== undefined && value !== null) data.append(key, value);
 });
 files.forEach((file) => data.append("documents", file));

 try {
 if (editing) {
 await updateStatutoryApproval(editing._id, data);
 toast.success("Statutory approval updated");
 } else {
 await createStatutoryApproval(data);
 toast.success("Statutory approval saved");
 }
 setShowForm(false);
 resetForm();
 await Promise.all([fetchRecords(), fetchStats()]);
 } catch (_) {
 toast.error("Unable to save statutory approval");
 }
 };

 const handleDelete = async (record) => {
 const confirm = await swal({
 title: "Delete statutory approval?",
 text: `${record.approvalType} will be removed.`,
 icon: "warning",
 buttons: true,
 dangerMode: true,
 });
 if (!confirm) return;

 try {
 await deleteStatutoryApproval(record._id);
 toast.success("Statutory approval deleted");
 await Promise.all([fetchRecords(), fetchStats()]);
 } catch (_) {
 toast.error("Delete failed");
 }
 };

 const handleDownloadPdf = async (record) => {
 try {
 setDownloadingPdf(record._id);
 const res = await downloadStatutoryApprovalPdf(record._id);
 const contentType = res.headers?.["content-type"] || "";
 if (contentType && !contentType.includes("application/pdf")) {
 toast.error("PDF generation failed");
 return;
 }

 const blob = new Blob([res.data], { type: "application/pdf" });
 const url = window.URL.createObjectURL(blob);
 const disposition = res.headers?.["content-disposition"] || "";
 const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
 const fileName =
 filenameMatch?.[1] ||
 `STATUTORY-APPROVAL-${record.approvalNo || record._id}.pdf`;

 const link = document.createElement("a");
 link.href = url;
 link.download = fileName;
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
 } catch (_) {
 toast.error("Unable to download PDF");
 } finally {
 setDownloadingPdf("");
 }
 };

 const changeFilter = (key, value) => {
 setFilters((prev) => ({ ...prev, [key]: value }));
 setPagination((prev) => ({ ...prev, currentPage: 1 }));
 };

 const statCards = stats
 ? [
 { label: "Total", value: stats.total, color: "primary", icon: "fa-file-contract" },
 { label: "Approved", value: stats.approved, color: "success", icon: "fa-check-circle" },
 { label: "Pending", value: stats.pending, color: "warning", icon: "fa-hourglass-half" },
 { label: "Expiring 30 Days", value: stats.expiringSoon, color: "info", icon: "fa-calendar-day" },
 { label: "Expired", value: stats.expired, color: "danger", icon: "fa-exclamation-triangle" },
 { label: "Docs Missing", value: stats.missingDocuments, color: "dark", icon: "fa-file-circle-xmark" },
 ]
 : [];

 return (
 <Fragment>
 <PageTitle activeMenu="Statutory Approvals" motherMenu="Document Control" />

 {stats && (
 <Row className="mb-3">
 {statCards.map((item) => (
 <Col key={item.label} xs={6} md={4} xl={2} className="mb-3">
 <Card className={`bg-${item.color} text-white shadow-sm h-100`}>
 <Card.Body className="p-3 d-flex align-items-center justify-content-between">
 <div>
 <p className="mb-1 small opacity-75">{item.label}</p>
 <h4 className="mb-0 fw-bold">{item.value}</h4>
 </div>
 <i className={`fas ${item.icon} fa-2x opacity-50`} />
 </Card.Body>
 </Card>
 </Col>
 ))}
 </Row>
 )}

 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white border-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
 <Card.Title className="mb-0">Approval Register</Card.Title>
 {canCreateStatutoryApproval && (
 <Button size="sm" variant="primary" onClick={openCreate}>
 <i className="fas fa-plus me-1" /> Add Approval
 </Button>
 )}
 </Card.Header>
 <Card.Body>
 <Row className="g-2 mb-3">
 <Col md={3}>
 <Form.Control
 size="sm"
 placeholder="Search certificate / authority"
 value={filters.search}
 onChange={(e) => changeFilter("search", e.target.value)}
 />
 </Col>
 <Col md={3}>
 <Form.Select size="sm" value={filters.project} onChange={(e) => changeFilter("project", e.target.value)}>
 <option value="">All Projects</option>
 {projects.map((project) => (
 <option key={project._id} value={project._id}>{project.projectName || project.projectCode}</option>
 ))}
 </Form.Select>
 </Col>
 <Col md={3}>
 <Form.Select size="sm" value={filters.approvalType} onChange={(e) => changeFilter("approvalType", e.target.value)}>
 <option value="">All Approval Types</option>
 {APPROVAL_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
 </Form.Select>
 </Col>
 <Col md={3}>
 <Form.Select size="sm" value={filters.status} onChange={(e) => changeFilter("status", e.target.value)}>
 <option value="">All Status</option>
 {STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
 </Form.Select>
 </Col>
 </Row>

 <div className="table-responsive">
 <Table hover className="align-middle">
 <thead>
 <tr>
 <th>Approval</th>
 <th>Project</th>
 <th>Work Order</th>
 <th>Certificate</th>
 <th>Validity</th>
 <th>Status</th>
 <th>Documents</th>
 <th className="text-end">Action</th>
 </tr>
 </thead>
 <tbody>
 {loading ? (
 <tr>
 <td colSpan="8" className="text-center py-4">
 <Spinner animation="border" size="sm" /> Loading...
 </td>
 </tr>
 ) : records.length ? records.map((record) => (
 <tr key={record._id}>
 <td>
 <div className="fw-bold">{record.approvalType}</div>
 <small className="text-muted">{record.approvalNo}</small>
 </td>
 <td>{record.project?.projectName || "—"}</td>
 <td>{record.workOrder?.woNo || record.workOrder?.woNumber || "—"}</td>
 <td>
 <div>{record.certificateNo || "-"}</div>
 <small className="text-muted">{record.authorityName || "-"}</small>
 </td>
 <td>
 <div><strong>Issue:</strong> {fmtDate(record.issueDate)}</div>
 <div><strong>Valid:</strong> {fmtDate(record.validTill)}</div>
 <small className="text-muted">Renewal: {fmtDate(record.renewalDueDate)}</small>
 </td>
 <td>
 <Badge bg={statusVariant(record.derivedStatus || record.status)}>{record.derivedStatus || record.status}</Badge>
 </td>
 <td>
 {(record.documents || []).length ? (
 <div className="d-flex flex-column gap-1">
 {record.documents.slice(0, 2).map((file, idx) => (
 <a key={`${file.fileUrl}-${idx}`} href={getFileUrl(file.fileUrl)} target="_blank" rel="noreferrer" className="small">
 <i className="fas fa-paperclip me-1" /> {file.fileName || `Document ${idx + 1}`}
 </a>
 ))}
 {record.documents.length > 2 && <small className="text-muted">+{record.documents.length - 2} more</small>}
 </div>
 ) : (
 <span className="text-danger small">Missing</span>
 )}
 </td>
 <td className="text-end">
 <div className="d-flex justify-content-end gap-1">
 <Button
 size="sm"
 variant="outline-danger"
 title="Download PDF"
 disabled={downloadingPdf === record._id}
 onClick={() => handleDownloadPdf(record)}
 >
 {downloadingPdf === record._id ? (
 <Spinner animation="border" size="sm" />
 ) : (
 <i className="fas fa-file-pdf" />
 )}
 </Button>
 {(canEditStatutoryApproval || canDeleteStatutoryApproval) && (
 <>
 {canEditStatutoryApproval && (
 <Button size="sm" variant="outline-primary" onClick={() => openEdit(record)}>
 <i className="fas fa-edit" />
 </Button>
 )}
 {canDeleteStatutoryApproval && (
 <Button size="sm" variant="outline-danger" onClick={() => handleDelete(record)}>
 <i className="fas fa-trash" />
 </Button>
 )}
 </>
 )}
 </div>
 </td>
 </tr>
 )) : (
 <tr>
 <td colSpan="7" className="text-center py-4 text-muted">No statutory approvals found</td>
 </tr>
 )}
 </tbody>
 </Table>
 </div>

 <div className="d-flex justify-content-between align-items-center">
 <small className="text-muted">{pagination.totalCount} records</small>
 <div className="d-flex gap-2">
 <Button
 size="sm"
 variant="outline-secondary"
 disabled={pagination.currentPage <= 1}
 onClick={() => setPagination((prev) => ({ ...prev, currentPage: prev.currentPage - 1 }))}
 >
 Previous
 </Button>
 <Button
 size="sm"
 variant="outline-secondary"
 disabled={pagination.currentPage >= pagination.totalPages}
 onClick={() => setPagination((prev) => ({ ...prev, currentPage: prev.currentPage + 1 }))}
 >
 Next
 </Button>
 </div>
 </div>
 </Card.Body>
 </Card>

 <Modal show={showForm} onHide={() => setShowForm(false)} size="lg" centered>
 <Form onSubmit={handleSubmit}>
 <Modal.Header closeButton>
 <Modal.Title>{editing ? "Edit Statutory Approval" : "Add Statutory Approval"}</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <Row className="g-3">
 <Col md={6}>
 <Form.Label>Project <span className="text-danger">*</span></Form.Label>
 <Form.Select value={form.project} onChange={(e) => setForm((prev) => ({ ...prev, project: e.target.value, workOrder: "" }))}>
 <option value="">Select Project</option>
 {projects.map((project) => <option key={project._id} value={project._id}>{project.projectName || project.projectCode}</option>)}
 </Form.Select>
 </Col>
 <Col md={6}>
 <Form.Label>Work Order</Form.Label>
 <Form.Select value={form.workOrder} onChange={(e) => setForm((prev) => ({ ...prev, workOrder: e.target.value }))}>
 <option value="">No Work Order</option>
 {visibleWorkOrders.map((wo) => <option key={wo._id} value={wo._id}>{(wo.woNo || wo.subject)}{wo.label ? ` – ${wo.label}` : ""}</option>)}
 </Form.Select>
 </Col>
 <Col md={6}>
 <Form.Label>Approval Type</Form.Label>
 <Form.Select value={form.approvalType} onChange={(e) => setForm((prev) => ({ ...prev, approvalType: e.target.value }))}>
 {APPROVAL_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
 </Form.Select>
 </Col>
 <Col md={6}>
 <Form.Label>Status</Form.Label>
 <Form.Select value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}>
 {STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
 </Form.Select>
 </Col>
 <Col md={6}>
 <Form.Label>Authority</Form.Label>
 <Form.Control value={form.authorityName} onChange={(e) => setForm((prev) => ({ ...prev, authorityName: e.target.value }))} />
 </Col>
 <Col md={6}>
 <Form.Label>Certificate No.</Form.Label>
 <Form.Control value={form.certificateNo} onChange={(e) => setForm((prev) => ({ ...prev, certificateNo: e.target.value }))} />
 </Col>
 <Col md={4}>
 <Form.Label>Issue Date</Form.Label>
 <Form.Control type="date" value={form.issueDate} onChange={(e) => setForm((prev) => ({ ...prev, issueDate: e.target.value }))} />
 </Col>
 <Col md={4}>
 <Form.Label>Valid Till</Form.Label>
 <Form.Control type="date" value={form.validTill} onChange={(e) => setForm((prev) => ({ ...prev, validTill: e.target.value }))} />
 </Col>
 <Col md={4}>
 <Form.Label>Renewal Due</Form.Label>
 <Form.Control type="date" value={form.renewalDueDate} onChange={(e) => setForm((prev) => ({ ...prev, renewalDueDate: e.target.value }))} />
 </Col>
 <Col md={12}>
 <Form.Label>Upload Documents</Form.Label>
 <Form.Control type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} />
 {editing?.documents?.length > 0 && (
 <div className="mt-2">
 <small className="text-muted d-block mb-1">Existing documents</small>
 {editing.documents.map((file, idx) => (
 <a key={`${file.fileUrl}-${idx}`} href={getFileUrl(file.fileUrl)} target="_blank" rel="noreferrer" className="badge bg-light text-primary me-1 mb-1">
 {file.fileName || `Document ${idx + 1}`}
 </a>
 ))}
 </div>
 )}
 </Col>
 <Col md={12}>
 <Form.Label>Remarks</Form.Label>
 <Form.Control as="textarea" rows={3} value={form.remarks} onChange={(e) => setForm((prev) => ({ ...prev, remarks: e.target.value }))} />
 </Col>
 </Row>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="light" onClick={() => setShowForm(false)}>Cancel</Button>
 <Button type="submit" variant="primary">{editing ? "Update" : "Save"}</Button>
 </Modal.Footer>
 </Form>
 </Modal>
 </Fragment>
 );
};

export default StatutoryApprovals;
